const RELIABILITY_VERDICT_LABELS = {
  operationally_unstable: 'At risk',
  operationally_stable_validation_needed: 'Stable, validation needed',
  reliable_for_covered_scenarios: 'Reliable for covered scenarios',
  low_confidence: 'Low confidence',
};

const ROLLUP_RELIABILITY_LABELS = {
  operationally_unstable: 'At-risk workflows',
  operationally_stable_validation_needed: 'Stable, validation needed',
  reliable_for_covered_scenarios: 'Reliable for covered scenarios',
  low_confidence: 'Low-confidence workflows',
};

const ROLLUP_RELIABILITY_ORDER = [
  'operationally_unstable',
  'operationally_stable_validation_needed',
  'reliable_for_covered_scenarios',
  'low_confidence',
];

export const rollupReliabilityHelperCopy =
  'Counts below are workflow verdict buckets, not finding totals. Each workflow appears in exactly one bucket.';

export function reliabilityVerdictLabel(overallVerdict) {
  return RELIABILITY_VERDICT_LABELS[overallVerdict] || 'Low confidence';
}

export function rollupReliabilityLabel(overallVerdict) {
  return ROLLUP_RELIABILITY_LABELS[overallVerdict] || reliabilityVerdictLabel(overallVerdict);
}

export function rollupReliabilityCountLabel(overallVerdict, count) {
  const workflowCount = typeof count === 'number' ? count : 0;
  switch (overallVerdict) {
    case 'operationally_unstable':
      return `${workflowCount} workflow${workflowCount === 1 ? '' : 's'} ${workflowCount === 1 ? 'is' : 'are'} at risk`;
    case 'operationally_stable_validation_needed':
      return `${workflowCount} workflow${workflowCount === 1 ? '' : 's'} ${workflowCount === 1 ? 'is' : 'are'} stable and ${
        workflowCount === 1 ? 'needs' : 'need'
      } validation`;
    case 'reliable_for_covered_scenarios':
      return `${workflowCount} workflow${workflowCount === 1 ? '' : 's'} ${workflowCount === 1 ? 'is' : 'are'} reliable for covered scenarios`;
    case 'low_confidence':
    default:
      return `${workflowCount} workflow${workflowCount === 1 ? '' : 's'} ${workflowCount === 1 ? 'is' : 'are'} currently low confidence`;
  }
}

export function rollupReliabilitySummaryItems(reliabilitySummary, { hideZeroCounts = false } = {}) {
  if (!reliabilitySummary) {
    return [];
  }

  const items = ROLLUP_RELIABILITY_ORDER.map((key) => {
    const count = reliabilitySummary.verdict_counts?.[key] || 0;
    return {
      key,
      label: rollupReliabilityLabel(key),
      count,
      accessibleLabel: rollupReliabilityCountLabel(key, count),
    };
  });

  return hideZeroCounts ? items.filter((item) => item.count > 0) : items;
}

function compactWorkflowCount(count, singular, plural = `${singular}s`) {
  const workflowCount = typeof count === 'number' ? count : 0;
  return `${workflowCount} ${workflowCount === 1 ? singular : plural}`;
}

export function portfolioReliabilityStatus(reliabilitySummary) {
  if (!reliabilitySummary) {
    return 'No workflow reliability verdicts are available yet.';
  }

  const verdictCounts = reliabilitySummary.verdict_counts || {};
  const parts = [
    verdictCounts.operationally_unstable
      ? compactWorkflowCount(verdictCounts.operationally_unstable, 'at-risk workflow')
      : null,
    verdictCounts.operationally_stable_validation_needed
      ? compactWorkflowCount(verdictCounts.operationally_stable_validation_needed, 'workflow needs validation', 'workflows need validation')
      : null,
    verdictCounts.reliable_for_covered_scenarios
      ? compactWorkflowCount(
          verdictCounts.reliable_for_covered_scenarios,
          'workflow reliable for covered scenarios',
          'workflows reliable for covered scenarios',
        )
      : null,
    verdictCounts.low_confidence
      ? compactWorkflowCount(verdictCounts.low_confidence, 'low-confidence workflow')
      : null,
  ].filter(Boolean);

  if (!parts.length) {
    return 'No workflow reliability verdicts are available yet.';
  }

  return `Current workflow verdicts: ${parts.join(', ')}.`;
}

export function reliabilityVerdictChipClassName(overallVerdict) {
  const normalizedVerdict = overallVerdict || 'low_confidence';
  return `chip verdict-chip verdict-chip-${normalizedVerdict}`;
}

export function operationalEvidenceSummary(reliabilityVerdict) {
  switch (reliabilityVerdict?.operational_status) {
    case 'unstable':
      return 'Active reliability issues are currently open in this workflow.';
    case 'stable':
      return 'Recent runtime evidence is stable enough for a bounded workflow-level claim.';
    case 'insufficient_data':
    default:
      return 'Recent runtime evidence is still too sparse for a trustworthy workflow-level claim.';
  }
}

export function validationCoverageSummary(reliabilityVerdict) {
  switch (reliabilityVerdict?.validation_coverage_status) {
    case 'covered':
      return 'Current targeted validation supports the scenarios that are covered.';
    case 'partial':
      return 'Targeted validation currently covers only part of the risky workflow behavior set.';
    case 'insufficient_data':
      return 'Targeted validation exists, but the available validation evidence is still too limited.';
    case 'unavailable':
    default:
      return 'No targeted validation coverage is available yet for this workflow.';
  }
}
